import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { formatPrice, formatDate, getStatusColor, getStatusText } from '../lib/utils';
import { toast } from 'sonner';
import {
  Wallet,
  TrendingUp,
  ShoppingBag,
  Clock,
  Plus,
  ArrowUpRight,
  Globe,
  Settings,
  BarChart3,
  CreditCard,
  Users,
  Gift,
  Copy,
  ExternalLink,
  RefreshCw,
  Download,
  Calendar,
  DollarSign,
  Package,
  Rocket,
  Palette,
  Image,
  Type,
  CheckCircle2,
  Loader2,
  Eye,
  Smartphone,
  Monitor,
  Zap,
  Upload,
  Store,
  Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Theme options for reseller subdomain stores (purely cosmetic, not tied to master brand)
const THEMES = [
  { id: 'fire',     name: 'Fire',          primary: '#FF0000', secondary: '#FFD700', bg: '#050A18' },
  { id: 'gold',     name: 'Gold',          primary: '#FFD700', secondary: '#FF0000', bg: '#050A18' },
  { id: 'midnight', name: 'Midnight',      primary: '#3B82F6', secondary: '#60A5FA', bg: '#050A18' },
  { id: 'emerald',  name: 'Emerald',       primary: '#10B981', secondary: '#34D399', bg: '#051A14' },
  { id: 'purple',   name: 'Purple Flame',  primary: '#8B5CF6', secondary: '#A78BFA', bg: '#0A0518' },
  { id: 'sunset',   name: 'Sunset',        primary: '#F97316', secondary: '#FBBF24', bg: '#0A0805' },
];

export default function ResellerDashboard() {
  const navigate = useNavigate();
  const { user, token, loading: authLoading, updateUser } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [topupAmount, setTopupAmount] = useState('');
  const [topupLoading, setTopupLoading] = useState(false);

  // Website Deploy States
  const [websiteSetup, setWebsiteSetup] = useState({
    storeName: '',
    customDomain: '',
    domainExtension: '.com',
    tagline: 'Top Up Game Murah & Cepat',
    whatsapp: '',
    theme: 'neon',
    logo: null,
    deployed: false,
    deploying: false,
    deployedAt: null,
    dnsConfigured: false,
  });
  const [setupStep, setSetupStep] = useState(1);
  const [previewDevice, setPreviewDevice] = useState('desktop');

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login?redirect=/reseller/dashboard');
      return;
    }
    if (user.role !== 'reseller' && user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchDashboard();
    // Load saved website config
    const savedConfig = localStorage.getItem(`website_config_${user.id}`);
    if (savedConfig) {
      setWebsiteSetup(JSON.parse(savedConfig));
    } else {
      setWebsiteSetup(prev => ({
        ...prev,
        storeName: `${user.name}'s Store`,
        customDomain: user.name.toLowerCase().replace(/\s+/g, ''),
        theme: 'fire', // Default theme for new reseller stores
      }));
    }
  }, [user, navigate]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/reseller/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Gagal memuat dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleTopup = async () => {
    const amount = parseFloat(topupAmount);
    if (!amount || amount <= 0) {
      toast.error('Masukkan jumlah yang valid');
      return;
    }

    setTopupLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/reseller/topup`,
        { amount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDashboard({ ...dashboard, balance: response.data.balance });
      updateUser({ ...user, balance: response.data.balance });
      toast.success(`Berhasil menambah saldo ${formatPrice(amount)}`);
      setTopupAmount('');
    } catch (error) {
      console.error('Failed to topup:', error);
      toast.error('Gagal top up saldo');
    } finally {
      setTopupLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Berhasil disalin!');
  };

  const handleDeployWebsite = async () => {
    if (!websiteSetup.storeName || !websiteSetup.customDomain) {
      toast.error('Lengkapi nama toko dan domain');
      return;
    }

    setWebsiteSetup(prev => ({ ...prev, deploying: true }));
    
    // Simulate deployment process
    await new Promise(resolve => setTimeout(resolve, 3000));

    const newConfig = {
      ...websiteSetup,
      deployed: true,
      deploying: false,
      deployedAt: new Date().toISOString(),
    };
    
    setWebsiteSetup(newConfig);
    localStorage.setItem(`website_config_${user.id}`, JSON.stringify(newConfig));
    toast.success('Website berhasil di-deploy! 🚀');
  };

  const handleSaveConfig = () => {
    localStorage.setItem(`website_config_${user.id}`, JSON.stringify(websiteSetup));
    toast.success('Konfigurasi tersimpan');
  };

  const selectedTheme = THEMES.find(t => t.id === websiteSetup.theme) || THEMES[0];

  if (!user || (user.role !== 'reseller' && user.role !== 'admin')) return null;

  if (loading) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  // Calculate metrics
  const todayOrders = dashboard?.recent_orders?.filter(o => {
    const today = new Date().toDateString();
    return new Date(o.created_at).toDateString() === today;
  }).length || 0;

  const completedOrders = dashboard?.recent_orders?.filter(o => o.status === 'completed').length || 0;

  return (
    <div className="min-h-screen pt-24 pb-12 cyber-grid">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 mb-2 px-3 py-1 rounded-full border border-primary/30 bg-primary/5">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
              <span className="font-mono text-[10px] text-primary tracking-widest uppercase">RESELLER_COMMAND_CONSOLE</span>
            </div>
            <h1 className="font-bold text-2xl md:text-3xl text-white uppercase tracking-wider">
              Dashboard Reseller
            </h1>
            <p className="text-muted-foreground text-xs font-mono">Selamat datang kembali, {user.name}!</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              className="border-border text-white hover:bg-white/5 font-mono text-xs uppercase"
              onClick={fetchDashboard}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button
              className="bg-primary hover:bg-primary/80 text-black font-bold uppercase tracking-widest neon-green text-xs"
              onClick={() => navigate('/')}
            >
              <Plus className="w-4 h-4 mr-2 text-black" />
              Order Baru
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-card/85 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card relative overflow-hidden">
            <div className="absolute top-0 left-0 w-2.5 h-2.5 border-t border-l border-primary/50" />
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center neon-green">
                <Wallet className="w-6 h-6 text-primary" />
              </div>
              <span className="text-[10px] font-mono text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-full uppercase tracking-wider">SALDO</span>
            </div>
            <p className="font-mono text-2xl md:text-3xl font-bold text-white">
              {formatPrice(dashboard?.balance || 0)}
            </p>
            <p className="text-xs font-mono text-muted-foreground mt-1">Saldo Tersedia</p>
          </div>

          <div className="bg-card/85 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card relative overflow-hidden">
            <div className="absolute top-0 left-0 w-2.5 h-2.5 border-t border-l border-primary/50" />
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center neon-green">
                <TrendingUp className="w-6 h-6 text-primary" />
              </div>
              <span className="text-[10px] font-mono text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-full flex items-center gap-1 uppercase tracking-wider">
                <ArrowUpRight className="w-3 h-3" />
                +15%
              </span>
            </div>
            <p className="font-mono text-2xl md:text-3xl font-bold text-white">
              {formatPrice(dashboard?.total_sales || 0)}
            </p>
            <p className="text-xs font-mono text-muted-foreground mt-1">Total Penjualan</p>
          </div>

          <div className="bg-card/85 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card relative overflow-hidden">
            <div className="absolute top-0 left-0 w-2.5 h-2.5 border-t border-l border-secondary/50" />
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-xl bg-secondary/20 flex items-center justify-center neon-magenta">
                <ShoppingBag className="w-6 h-6 text-secondary" />
              </div>
              <span className="text-[10px] font-mono text-muted-foreground">{todayOrders} hari ini</span>
            </div>
            <p className="font-mono text-2xl md:text-3xl font-bold text-white">
              {dashboard?.total_orders || 0}
            </p>
            <p className="text-xs font-mono text-muted-foreground mt-1">Total Transaksi</p>
          </div>

          <div className="bg-card/85 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card relative overflow-hidden">
            <div className="absolute top-0 left-0 w-2.5 h-2.5 border-t border-l border-accent/50" />
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center">
                <Globe className="w-6 h-6 text-accent" />
              </div>
              {websiteSetup.deployed ? (
                <span className="text-[10px] font-mono text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-full uppercase tracking-wider">ONLINE</span>
              ) : (
                <span className="text-[10px] font-mono text-muted-foreground bg-muted border border-border/20 px-2 py-0.5 rounded-full uppercase tracking-wider">OFFLINE</span>
              )}
            </div>
            <p className="font-mono text-sm md:text-base font-bold text-white truncate">
              {websiteSetup.customDomain || 'belum-setup'}
            </p>
            <p className="text-xs font-mono text-muted-foreground mt-1">{websiteSetup.domainExtension || '.com'}</p>
          </div>
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-black/40 border border-border p-1.5 font-mono text-xs">
            <TabsTrigger value="overview" className="data-[state=active]:bg-primary data-[state=active]:text-black text-[10px] font-bold uppercase tracking-wider transition-all duration-300">
              <BarChart3 className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="orders" className="data-[state=active]:bg-primary data-[state=active]:text-black text-[10px] font-bold uppercase tracking-wider transition-all duration-300">
              <ShoppingBag className="w-4 h-4 mr-2" />
              Transaksi
            </TabsTrigger>
            <TabsTrigger value="topup" className="data-[state=active]:bg-primary data-[state=active]:text-black text-[10px] font-bold uppercase tracking-wider transition-all duration-300">
              <CreditCard className="w-4 h-4 mr-2" />
              Top Up Saldo
            </TabsTrigger>
            <TabsTrigger value="deploy" className="data-[state=active]:bg-primary data-[state=active]:text-black text-[10px] font-bold uppercase tracking-wider transition-all duration-300">
              <Rocket className="w-4 h-4 mr-2" />
              Deploy Website
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Chart placeholder */}
              <div className="lg:col-span-2 bg-card/85 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card relative overflow-hidden">
                <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-primary/50" />
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-bold text-lg text-white uppercase tracking-wider">
                    Statistik Penjualan
                  </h3>
                  <select className="bg-black/50 border border-border rounded-lg px-3 py-1.5 text-xs text-white font-mono">
                    <option>7 Hari Terakhir</option>
                    <option>30 Hari Terakhir</option>
                    <option>Bulan Ini</option>
                  </select>
                </div>
                <div className="h-64 flex items-center justify-center bg-black/20 rounded-xl border border-border/30">
                  <div className="text-center font-mono">
                    <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-2 animate-pulse" />
                    <p className="text-muted-foreground text-xs uppercase tracking-wide">Grafik penjualan akan ditampilkan di sini</p>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-card/85 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card relative overflow-hidden">
                <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-secondary/50" />
                <h3 className="font-bold text-lg text-white uppercase mb-4 tracking-wider">
                  Aksi Cepat
                </h3>
                <div className="space-y-3 font-mono text-xs">
                  <Button 
                    className="w-full justify-start bg-white/5 hover:bg-white/10 text-white border border-border/40 uppercase tracking-wider"
                    onClick={() => navigate('/')}
                  >
                    <Plus className="w-4 h-4 mr-3 text-primary" />
                    Buat Order Baru
                  </Button>
                  <Button 
                    className="w-full justify-start bg-white/5 hover:bg-white/10 text-white border border-border/40 uppercase tracking-wider"
                    onClick={() => setActiveTab('topup')}
                  >
                    <Wallet className="w-4 h-4 mr-3 text-primary" />
                    Top Up Saldo
                  </Button>
                  <Button 
                    className="w-full justify-start bg-white/5 hover:bg-white/10 text-white border border-border/40 uppercase tracking-wider"
                    onClick={() => setActiveTab('deploy')}
                  >
                    <Rocket className="w-4 h-4 mr-3 text-secondary" />
                    {websiteSetup.deployed ? 'Kelola Website' : 'Deploy Website'}
                  </Button>
                  <Button 
                    className="w-full justify-start bg-white/5 hover:bg-white/10 text-white border border-border/40 uppercase tracking-wider"
                  >
                    <Gift className="w-4 h-4 mr-3 text-accent" />
                    Buat Kupon
                  </Button>
                </div>

                {/* Referral Link */}
                <div className="mt-6 p-4 bg-primary/5 rounded-xl border border-primary/20 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-primary/50" />
                  <p className="text-[10px] font-mono text-muted-foreground mb-2 uppercase tracking-wide">Link Referral Anda</p>
                  <div className="flex items-center gap-2">
                    <Input 
                      value={`https://voucherverse.com/ref/${user?.id?.slice(0, 8)}`}
                      readOnly
                      className="bg-black/30 border-border text-white text-xs font-mono"
                    />
                    <Button 
                      size="icon"
                      className="bg-primary hover:bg-primary/80 text-black neon-green"
                      onClick={() => copyToClipboard(`https://voucherverse.com/ref/${user?.id?.slice(0, 8)}`)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Orders */}
            <div className="bg-card/85 backdrop-blur-sm rounded-xl border border-border cyber-card relative overflow-hidden overflow-hidden">
              <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-primary/50" />
              <div className="p-6 border-b border-border/45 flex items-center justify-between">
                <h3 className="font-bold text-lg text-white uppercase tracking-wider">
                  Transaksi Terbaru
                </h3>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="text-primary font-mono text-xs uppercase"
                  onClick={() => setActiveTab('orders')}
                >
                  Lihat Semua
                  <ArrowUpRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              {dashboard?.recent_orders?.length > 0 ? (
                <div className="divide-y divide-border/40 font-mono text-xs">
                  {dashboard.recent_orders.slice(0, 5).map((order) => (
                    <div key={order.id} className="p-4 hover:bg-white/5 transition-colors flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                          <Package className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-bold text-white">{order.product_name}</p>
                          <p className="text-[10px] text-muted-foreground mt-0.5">{order.denomination_name}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-white mb-1">{formatPrice(order.price)}</p>
                        <span className={`px-2 py-0.5 text-[9px] font-bold rounded-full ${getStatusColor(order.status)}`}>
                          {getStatusText(order.status)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center font-mono text-xs">
                  <ShoppingBag className="w-12 h-12 text-muted-foreground mx-auto mb-4 animate-bounce" />
                  <p className="text-muted-foreground uppercase tracking-wide">Belum ada transaksi</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <div className="bg-card/85 backdrop-blur-sm rounded-xl border border-border cyber-card relative overflow-hidden overflow-hidden">
              <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-primary/50" />
              <div className="p-6 border-b border-border/45 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <h3 className="font-bold text-lg text-white uppercase tracking-wider">
                  Riwayat Transaksi
                </h3>
                <div className="flex items-center gap-3">
                  <Input 
                    placeholder="Cari order..." 
                    className="w-48 bg-black/35 border-border font-mono text-xs"
                  />
                  <Button variant="outline" className="border-border font-mono text-xs uppercase">
                    <Download className="w-4 h-4 mr-2" />
                    Export
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/40 font-mono text-xs">
                      <TableHead className="text-muted-foreground uppercase">Order</TableHead>
                      <TableHead className="text-muted-foreground uppercase">Produk</TableHead>
                      <TableHead className="text-muted-foreground uppercase">User ID</TableHead>
                      <TableHead className="text-muted-foreground uppercase">Total</TableHead>
                      <TableHead className="text-muted-foreground uppercase">Status</TableHead>
                      <TableHead className="text-muted-foreground uppercase">Tanggal</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody className="font-mono text-xs">
                    {dashboard?.recent_orders?.map((order) => (
                      <TableRow key={order.id} className="border-border/40 hover:bg-white/5 transition-colors">
                        <TableCell className="font-bold">{order.order_number}</TableCell>
                        <TableCell>
                          <div>
                            <p className="text-white font-bold">{order.product_name}</p>
                            <p className="text-[10px] text-muted-foreground">{order.denomination_name}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-300">{order.game_user_id}</TableCell>
                        <TableCell className="text-primary font-bold">{formatPrice(order.price)}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-0.5 text-[9px] font-bold rounded-full ${getStatusColor(order.status)}`}>
                            {getStatusText(order.status)}
                          </span>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(order.created_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* Top Up Tab */}
          <TabsContent value="topup">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-card/85 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card relative overflow-hidden">
                <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-primary/50" />
                <h3 className="font-bold text-lg text-white uppercase mb-6 tracking-wider">
                  Top Up Saldo
                </h3>
                <div className="space-y-4 font-mono text-xs">
                  <div>
                    <label className="text-xs text-muted-foreground mb-2 block uppercase">▸ Jumlah Top Up</label>
                    <Input
                      type="number"
                      placeholder="Masukkan jumlah"
                      className="bg-black/50 border-border text-white font-mono text-base focus:border-primary focus:ring-1 focus:ring-primary/30"
                      value={topupAmount}
                      onChange={(e) => setTopupAmount(e.target.value)}
                      data-testid="topup-amount"
                    />
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {[100000, 250000, 500000, 1000000].map((amount) => (
                      <Button
                        key={amount}
                        variant="outline"
                        size="sm"
                        className="border-border text-white hover:bg-white/5 font-mono text-[10px]"
                        onClick={() => setTopupAmount(amount.toString())}
                      >
                        {formatPrice(amount)}
                      </Button>
                    ))}
                  </div>

                  <div className="bg-black/35 rounded-xl p-4 border border-border/20">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-muted-foreground">Saldo Saat Ini</span>
                      <span className="font-mono text-white">{formatPrice(dashboard?.balance || 0)}</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-muted-foreground">Top Up</span>
                      <span className="font-mono text-primary">+{formatPrice(parseFloat(topupAmount) || 0)}</span>
                    </div>
                    <div className="border-t border-border/40 pt-2 mt-2">
                      <div className="flex justify-between items-center">
                        <span className="text-white font-medium">Saldo Akhir</span>
                        <span className="font-mono text-base text-primary font-bold">
                          {formatPrice((dashboard?.balance || 0) + (parseFloat(topupAmount) || 0))}
                        </span>
                      </div>
                    </div>
                  </div>

                  <Button
                    className="w-full bg-primary hover:bg-primary/80 text-black font-bold uppercase tracking-widest py-6 neon-green text-xs"
                    onClick={handleTopup}
                    disabled={topupLoading}
                    data-testid="topup-submit"
                  >
                    {topupLoading ? 'Memproses...' : '[ Top Up Saldo (Demo) ]'}
                  </Button>
                  <p className="text-[10px] text-muted-foreground text-center uppercase">
                    * Ini adalah simulasi transmisi dana demonstrasi
                  </p>
                </div>
              </div>

              <div className="bg-card/85 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card relative overflow-hidden">
                <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-secondary/50" />
                <h3 className="font-bold text-lg text-white uppercase mb-6 tracking-wider">
                  Riwayat Top Up
                </h3>
                <div className="space-y-3 font-mono text-xs">
                  {[
                    { date: '10 Feb 2026', amount: 500000, status: 'success' },
                    { date: '8 Feb 2026', amount: 250000, status: 'success' },
                    { date: '5 Feb 2026', amount: 1000000, status: 'success' },
                  ].map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-black/30 border border-border/20 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center neon-green">
                          <DollarSign className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-bold text-white">+{formatPrice(item.amount)}</p>
                          <p className="text-[10px] text-muted-foreground mt-0.5">{item.date}</p>
                        </div>
                      </div>
                      <span className="text-[10px] text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-full uppercase tracking-wider font-bold">Berhasil</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Deploy Website Tab */}
          <TabsContent value="deploy" className="space-y-6">
            {/* Status Banner */}
            {websiteSetup.deployed && (
              <div className="bg-primary/5 border border-primary/30 rounded-xl p-6 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-primary/60" />
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center neon-green">
                      <CheckCircle2 className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-white uppercase tracking-wider">Website Online!</h3>
                      <p className="text-xs text-white font-mono mt-0.5">
                        {websiteSetup.customDomain}{websiteSetup.domainExtension}
                      </p>
                      <p className="text-[10px] text-muted-foreground font-mono mt-0.5">
                        Deployed pada {formatDate(websiteSetup.deployedAt)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 font-mono text-xs">
                    <Button
                      variant="outline"
                      className="border-primary/30 text-primary hover:bg-primary/10 uppercase tracking-wider"
                      onClick={() => window.open(`https://${websiteSetup.customDomain}${websiteSetup.domainExtension}`, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Buka Website
                    </Button>
                    <Button
                      variant="outline"
                      className="border-border uppercase tracking-wider"
                      onClick={() => copyToClipboard(`https://${websiteSetup.customDomain}${websiteSetup.domainExtension}`)}
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Salin URL
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Configuration */}
              <div className="bg-card/85 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card relative overflow-hidden">
                <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-primary/50" />
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center neon-green">
                    <Settings className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-white uppercase tracking-wider">
                      Konfigurasi Website
                    </h3>
                    <p className="text-xs text-muted-foreground font-mono">Sesuaikan tampilan toko Anda</p>
                  </div>
                </div>

                <div className="space-y-5">
                  {/* Store Name */}
                  <div className="space-y-2">
                    <Label className="text-xs font-mono text-muted-foreground uppercase flex items-center gap-2">
                      <Store className="w-4 h-4" />
                      Nama Toko
                    </Label>
                    <Input
                      placeholder="Nama toko Anda"
                      className="bg-black/50 border-border text-white font-mono text-xs focus:border-primary focus:ring-1 focus:ring-primary/30"
                      value={websiteSetup.storeName}
                      onChange={(e) => setWebsiteSetup(prev => ({ ...prev, storeName: e.target.value }))}
                    />
                  </div>

                  {/* Custom Domain */}
                  <div className="space-y-2">
                    <Label className="text-xs font-mono text-muted-foreground uppercase flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Domain Website
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="namatoko"
                        className="bg-black/50 border-border text-white flex-1 font-mono text-xs focus:border-primary focus:ring-1 focus:ring-primary/30"
                        value={websiteSetup.customDomain}
                        onChange={(e) => setWebsiteSetup(prev => ({ 
                          ...prev, 
                          customDomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') 
                        }))}
                      />
                      <select
                        className="bg-black/50 border border-border rounded-lg px-3 text-white text-xs font-mono focus:border-primary focus:ring-1 focus:ring-primary/30"
                        value={websiteSetup.domainExtension}
                        onChange={(e) => setWebsiteSetup(prev => ({ ...prev, domainExtension: e.target.value }))}
                      >
                        <option value=".com" className="bg-neutral-900">.com</option>
                        <option value=".id" className="bg-neutral-900">.id</option>
                        <option value=".co.id" className="bg-neutral-900">.co.id</option>
                        <option value=".net" className="bg-neutral-900">.net</option>
                        <option value=".store" className="bg-neutral-900">.store</option>
                      </select>
                    </div>
                    <p className="text-[10px] text-muted-foreground font-mono">
                      Domain: <span className="text-primary font-bold">{websiteSetup.customDomain || 'namatoko'}{websiteSetup.domainExtension}</span>
                    </p>
                  </div>

                  {/* DNS Configuration Info */}
                  {!websiteSetup.deployed && (
                    <div className="bg-secondary/5 border border-secondary/20 rounded-xl p-4 relative overflow-hidden">
                      <div className="absolute top-0 left-0 w-2.5 h-2.5 border-t border-l border-secondary/50" />
                      <div className="flex items-start gap-3">
                        <Globe className="w-5 h-5 text-secondary mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-white font-mono font-bold mb-1 uppercase">Setup DNS (Setelah Deploy)</p>
                          <p className="text-[10px] text-muted-foreground font-mono mb-2">
                            Setelah deploy, arahkan domain Anda ke server kami dengan menambahkan DNS record berikut:
                          </p>
                          <div className="bg-black/30 rounded-lg p-2 font-mono text-[10px]">
                            <p className="text-gray-300">A Record: @ → 185.199.108.153</p>
                            <p className="text-gray-300">CNAME: www → hosting.voucherverse.com</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Tagline */}
                  <div className="space-y-2">
                    <Label className="text-xs font-mono text-muted-foreground uppercase flex items-center gap-2">
                      <Type className="w-4 h-4" />
                      Tagline
                    </Label>
                    <Input
                      placeholder="Slogan toko Anda"
                      className="bg-black/50 border-border text-white font-mono text-xs focus:border-primary focus:ring-1 focus:ring-primary/30"
                      value={websiteSetup.tagline}
                      onChange={(e) => setWebsiteSetup(prev => ({ ...prev, tagline: e.target.value }))}
                    />
                  </div>

                  {/* WhatsApp */}
                  <div className="space-y-2">
                    <Label className="text-xs font-mono text-muted-foreground uppercase flex items-center gap-2">
                      <Smartphone className="w-4 h-4" />
                      WhatsApp Support
                    </Label>
                    <Input
                      placeholder="08xxxxxxxxxx"
                      className="bg-black/50 border-border text-white font-mono text-xs focus:border-primary focus:ring-1 focus:ring-primary/30"
                      value={websiteSetup.whatsapp}
                      onChange={(e) => setWebsiteSetup(prev => ({ ...prev, whatsapp: e.target.value }))}
                    />
                  </div>

                  {/* Theme Selection */}
                  <div className="space-y-2">
                    <Label className="text-xs font-mono text-muted-foreground uppercase flex items-center gap-2">
                      <Palette className="w-4 h-4" />
                      Tema Website
                    </Label>
                    <div className="grid grid-cols-3 gap-2">
                      {THEMES.map((theme) => (
                        <button
                          key={theme.id}
                          className={`p-3 rounded-xl border transition-all ${
                            websiteSetup.theme === theme.id 
                              ? 'border-primary bg-primary/10' 
                              : 'border-border hover:border-primary/30'
                          }`}
                          onClick={() => setWebsiteSetup(prev => ({ ...prev, theme: theme.id }))}
                        >
                          <div className="flex gap-1 mb-2">
                            <div 
                              className="w-4 h-4 rounded-full" 
                              style={{ backgroundColor: theme.primary }}
                            />
                            <div 
                              className="w-4 h-4 rounded-full" 
                              style={{ backgroundColor: theme.secondary }}
                            />
                          </div>
                          <p className="text-[10px] text-white text-left font-mono">{theme.name}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-4 font-mono text-xs">
                    <Button
                      variant="outline"
                      className="flex-1 border-border uppercase tracking-wider"
                      onClick={handleSaveConfig}
                    >
                      Simpan Draft
                    </Button>
                    <Button
                      className="flex-1 bg-primary hover:bg-primary/80 text-black font-bold uppercase tracking-widest neon-green"
                      onClick={handleDeployWebsite}
                      disabled={websiteSetup.deploying}
                    >
                      {websiteSetup.deploying ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Deploying...
                        </>
                      ) : websiteSetup.deployed ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 text-black" />
                          Update Website
                        </>
                      ) : (
                        <>
                          <Rocket className="w-4 h-4 mr-2 text-black" />
                          Deploy Website
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div className="bg-card/85 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card relative overflow-hidden">
                <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-secondary/50" />
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center neon-magenta">
                      <Eye className="w-5 h-5 text-secondary" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-white uppercase tracking-wider">
                        Preview Website
                      </h3>
                      <p className="text-xs text-muted-foreground font-mono">Lihat tampilan website Anda</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 bg-black/30 rounded-lg p-1">
                    <button
                      className={`p-2 rounded ${previewDevice === 'desktop' ? 'bg-white/10' : ''}`}
                      onClick={() => setPreviewDevice('desktop')}
                    >
                      <Monitor className="w-4 h-4 text-white" />
                    </button>
                    <button
                      className={`p-2 rounded ${previewDevice === 'mobile' ? 'bg-white/10' : ''}`}
                      onClick={() => setPreviewDevice('mobile')}
                    >
                      <Smartphone className="w-4 h-4 text-white" />
                    </button>
                  </div>
                </div>

                {/* Preview Frame */}
                <div 
                  className={`rounded-xl overflow-hidden border-4 border-white/10 transition-all mx-auto ${
                    previewDevice === 'mobile' ? 'w-[280px]' : 'w-full'
                  }`}
                  style={{ backgroundColor: selectedTheme.bg }}
                >
                  {/* Preview Header */}
                  <div 
                    className="p-4 border-b border-white/10"
                    style={{ backgroundColor: `${selectedTheme.primary}10` }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-8 h-8 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: selectedTheme.primary }}
                        >
                          <Zap className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-bold text-white text-sm">
                          {websiteSetup.storeName || 'Nama Toko'}
                        </span>
                      </div>
                      {previewDevice === 'desktop' && (
                        <div className="flex items-center gap-4 text-xs text-white/70">
                          <span>Home</span>
                          <span>Produk</span>
                          <span>Cek Transaksi</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Preview Hero */}
                  <div className="p-6 text-center">
                    <h2 
                      className="font-bold text-lg mb-2"
                      style={{ color: selectedTheme.primary }}
                    >
                      {websiteSetup.storeName || 'Nama Toko'}
                    </h2>
                    <p className="text-white/70 text-xs mb-4">
                      {websiteSetup.tagline}
                    </p>
                    <div 
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs"
                      style={{ backgroundColor: `${selectedTheme.secondary}20`, color: selectedTheme.secondary }}
                    >
                      <Sparkles className="w-3 h-3" />
                      Proses Instant 24 Jam
                    </div>
                  </div>

                  {/* Preview Products */}
                  <div className="p-4">
                    <p className="text-white/50 text-xs mb-3 uppercase tracking-wider">Produk Populer</p>
                    <div className={`grid gap-2 ${previewDevice === 'mobile' ? 'grid-cols-2' : 'grid-cols-4'}`}>
                      {['ML', 'FF', 'PUBG', 'GI'].map((game) => (
                        <div 
                          key={game}
                          className="p-3 rounded-lg border border-white/10 text-center"
                          style={{ backgroundColor: `${selectedTheme.primary}10` }}
                        >
                          <div 
                            className="w-8 h-8 rounded-lg mx-auto mb-2 flex items-center justify-center text-xs font-bold"
                            style={{ backgroundColor: selectedTheme.primary, color: 'white' }}
                          >
                            {game}
                          </div>
                          <p className="text-white/70 text-xs">{game}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Preview Footer */}
                  <div className="p-4 border-t border-white/10">
                    <p className="text-white/30 text-xs text-center">
                      © 2026 {websiteSetup.storeName || 'Nama Toko'}
                    </p>
                  </div>
                </div>

                {/* Preview URL */}
                <div className="mt-4 p-3 bg-black/20 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-mono text-white">
                      {websiteSetup.customDomain || 'namatoko'}{websiteSetup.domainExtension}
                    </span>
                  </div>
                  {websiteSetup.deployed && (
                    <span className="text-xs text-primary flex items-center gap-1 font-mono uppercase tracking-wider font-bold">
                      <CheckCircle2 className="w-3 h-3 text-primary" />
                      Live
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Features Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 font-mono text-xs">
              <div className="bg-card/85 backdrop-blur-sm rounded-xl p-4 border border-border flex items-start gap-3 cyber-card relative overflow-hidden">
                <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-primary/50" />
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0 neon-green">
                  <Zap className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-bold text-white mb-1 uppercase">Instant Deploy</h4>
                  <p className="text-[10px] text-muted-foreground">Website langsung online dalam hitungan detik</p>
                </div>
              </div>
              <div className="bg-card/85 backdrop-blur-sm rounded-xl p-4 border border-border flex items-start gap-3 cyber-card relative overflow-hidden">
                <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-secondary/50" />
                <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center flex-shrink-0 neon-magenta">
                  <Globe className="w-5 h-5 text-secondary" />
                </div>
                <div>
                  <h4 className="font-bold text-white mb-1 uppercase">Custom Domain</h4>
                  <p className="text-[10px] text-muted-foreground">Domain sendiri (.com, .id, .store) dengan SSL gratis</p>
                </div>
              </div>
              <div className="bg-card/85 backdrop-blur-sm rounded-xl p-4 border border-border flex items-start gap-3 cyber-card relative overflow-hidden">
                <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-primary/50" />
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0 neon-green">
                  <RefreshCw className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-bold text-white mb-1 uppercase">Auto Sync</h4>
                  <p className="text-[10px] text-muted-foreground">Produk &amp; harga sinkron otomatis dengan platform pusat</p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
